import React from 'react'
import { useTranslation } from 'react-i18next'
import { Location, useLocation } from 'react-router-dom'
import clsx from 'clsx'

import { cleanEmail, OrgCardAdapter } from '@makyoapp/makyo-common'
import {
  AvatarSize,
  Badge,
  Sidebar as BaseSidebar,
  SidebarProps as BaseSidebarProps,
  ColorVariant,
  FallbackAvatar,
  getOrgRoleIcon,
  NavItem,
  Tip,
  useTip,
} from '@makyoapp/makyo-ui'

import { useAppContext } from '@/context/AppContext'
import { useCacheContext } from '@/context/CacheContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgsContext } from '@/context/OrgsContext'
import useAuth from '@/hooks/useAuth'
import { DEFAULT_SIDEBAR_SIZE } from '@/shared/constants'
import { RoutePropsWithComponent } from '@/shared/routes'

import Ic from '../Ic'
import SlideMenu from './SlideMenu'

type RoutePropsWithoutComponent = Omit<RoutePropsWithComponent, 'component'>

export type MenuItem = Pick<RoutePropsWithoutComponent, 'path' | 'name' | 'icon' | 'index' | 'showOnNav' | 'withBorder'>

/**
 * Sidebar Menu params
 */
interface SidebarMenuProps {
  /**
   * array/collections of routes that will be turned into menu
   */
  menu: MenuItem[]
  /**
   * after menu
   */
  after?: React.ReactNode
  /**
   * extra content after the menu
   */
  children?: React.ReactNode
  /**
   * @param path
   * function callback to pre-process the [to] params for `<NavLink>`
   * eq:
   * getTo={(path) => (path + '/' + id ) }
   */
  getTo: (path: string, location: Location) => string

  /**
   * show org header
   */
  org?: Pick<OrgCardAdapter, 'name' | 'avatar'>

  /**
   * show account avatar
   */
  showAvatar?: boolean
}

function OrgHeader() {
  const { currentOrg: org } = useCurrentOrgContext()
  const { userData } = useAuth()

  const { recentApps } = useOrgsContext()

  const plan = recentApps.find((o) => o.org.id === org?.id)?.plan || { name: 'FREE', badgeColor: ColorVariant.Yellow }

  const { imageCache } = useCacheContext()
  const cachedAvatar = !!org && !!imageCache.values[`org-${org.id}`]

  const userId = userData?.id
  const member = org?.members.find((m) => m.userId === userId)
  const orgName = org?.name
  const longOrgName = !!orgName && orgName.length > 20

  const { x, y, refs, context, placement, strategy, isOpen, arrowRef, getReferenceProps, getFloatingProps } = useTip({
    placement: 'bottom',
    matchWidth: longOrgName,
  })

  if (!org) {
    return null
  }

  return (
    <div className='flex gap-3 items-center justify-between bg-zinc-100 dark:bg-slate-700 px-4 py-2'>
      <FallbackAvatar
        size={AvatarSize.M}
        src={org.avatar}
        text={org.name}
        alt={org.name}
        className='border'
        withCache={cachedAvatar}
      />

      <div className='flex-1 min-w-0'>
        {/* Users' org name with tooltip */}
        <div className='mb-1 flex gap-2'>
          <span ref={refs.setReference} className='font-bold txt cursor-default' {...getReferenceProps()}>
            {orgName}
          </span>

          <Badge color={plan?.badgeColor as ColorVariant} className='text-xs'>
            {plan?.name}
          </Badge>

          {isOpen && (
            <Tip
              {...getFloatingProps()}
              x={x}
              y={y}
              placement={placement}
              strategy={strategy}
              ref={refs.setFloating}
              context={context}
              arrowRef={arrowRef}
              className='z-101!'
            >
              {orgName}
            </Tip>
          )}
        </div>

        {/* Users' role */}
        {member && (
          <div className='flex items-center gap-1 uppercase text-m-sm text-light'>
            {getOrgRoleIcon(member.orgRole)}
            <span>{member.orgRole}</span>
          </div>
        )}
      </div>

      {/* <div>
        <Badge color={planColor}>{plan}</Badge>
      </div> */}
    </div>
  )
}

function UserHeader() {
  const { userData } = useAuth()

  // Add timestamp state for cache busting
  const [timestamp] = React.useState<number>(Date.now())

  if (userData) {
    const clean = cleanEmail(userData.email)

    // Create timestamped avatar URL
    const avatarWithTimestamp =
      userData.avatar && !userData.avatar.startsWith('blob:') ? `${userData.avatar}?t=${timestamp}` : userData.avatar

    return (
      <div className='flex-c bg-zinc-100 dark:bg-slate-700 px-4 py-2'>
        <FallbackAvatar
          size={AvatarSize.XS}
          src={avatarWithTimestamp}
          email={userData.email}
          faceAvatar={true}
          text={clean}
          alt={clean}
        />
        <div className='font-bold txt'>{userData.name || clean}</div>
      </div>
    )
  }

  return null
}

function Menu({ menu, getTo, after }: Pick<SidebarMenuProps, 'menu' | 'getTo' | 'after'>) {
  const { t } = useTranslation()
  const location = useLocation()

  return (
    <>
      {menu.map((route, index) => {
        return route.showOnNav !== false ? (
          <React.Fragment key={`${route.path}-${index}`}>
            {route.withBorder === true && <hr className='hr' />}
            <NavItem
              end={route.index}
              text={t(route.name)}
              icon={<Ic tag={route.icon} width={16} height={16} />}
              to={getTo ? getTo?.(route.index ? '' : route.path, location) : route.index ? '' : route.path}
            />
          </React.Fragment>
        ) : null
      })}
      {after && (
        <>
          <hr className='hr' />
          {after}
        </>
      )}
    </>
  )
}

/**
 * Sidebar menu
 */
export default function SidebarMenu({ menu, children, getTo, showAvatar }: SidebarMenuProps) {
  return (
    <>
      <div className='flex flex-col justify-between h-full'>
        <div>
          <OrgHeader />

          <hr className='hr' />

          {showAvatar && (
            <>
              <UserHeader />
              <hr className='hr' />
            </>
          )}

          <Menu menu={menu} getTo={getTo} />
        </div>

        <div>
          {children && (
            <>
              <hr className='hr' />
              {children}
            </>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * Sidebar menu
 */
export function SidebarMenuAlt({ menu, children, getTo }: SidebarMenuProps) {
  return (
    <>
      <Menu menu={menu} getTo={getTo} />
      {children && (
        <>
          <hr className='hr' />
          {children}
        </>
      )}
    </>
  )
}

interface SidebarProps {
  resizeable?: boolean
  menu?: React.ReactNode
  className?: string
  baseSidebarProps?: BaseSidebarProps
}

export function Sidebar({ resizeable = false, menu, className, baseSidebarProps }: SidebarProps) {
  const { showSlideMenu, toggleSlideMenu } = useAppContext()
  return (
    <>
      <BaseSidebar
        className={clsx(
          'bg-white dark:bg-transparent',
          !resizeable && 'hidden md:block mk-scroll overflow-y-auto',
          className
        )}
        resizable={resizeable}
        maxWidth={DEFAULT_SIDEBAR_SIZE}
        initialWidth={DEFAULT_SIDEBAR_SIZE}
        {...baseSidebarProps}
      >
        {menu}
      </BaseSidebar>
      {showSlideMenu && (
        <SlideMenu isOpen={showSlideMenu} onClose={toggleSlideMenu}>
          {menu}
        </SlideMenu>
      )}
    </>
  )
}
